import React from 'react';
import './UsageTransferDescription.css'; // External CSS for styling

const UsageTransferDescription: React.FC = () => {
  return (
    <div className="report-description">
      <section>
        <h4 className="subheader">Description:</h4>
        <p className="text">
          The Usage Transfer report identifies products within an expiration window that aren’t expected to be used prior to expiry. The determination of which items are likely to be used is based on the previous 12 months of usage and the current percentage over PAR.  The report then checks inventory across other departments in your hospital system, identifies where the items could be transferred, and recommends transfer amounts based on each department’s gap to PAR.
        </p>
      </section>

      <section>
        <h4 className="subheader">Refresh Schedule:</h4>
        <p className="text">
          This report refreshes every 15 minutes.  The last refresh time can be seen at the top right corner of the report.
        </p>
      </section>

      <section>
        <h4 className="subheader">Calculated Fields and Assumptions:</h4>

        <h5 className="subheader-italic">Metric Cards:</h5>
        <ul className="text">
          <li><span className="label">What Can I Move:</span> Total quantity of expiring products that are eligible to transfer that exceed PAR and will not be used based on historical usage from the same period as the expiring bucket selected (i.e. 7, 30, 60, 90 or 180 days).</li>
          <li><span className="label">How Much Will Be Saved:</span> The value of all eligible transfer options based on their current price.</li>
          <li><span className="label">What Expires Soon:</span> Total quantity of products that will expire within the applied expiration bucket (i.e., 7, 30, 60, 90, or 180 days).</li>
          <li><span className="label">How Much Will Be Wasted:</span> The value of all products that will expire based on their current price.</li>
        </ul>

        <h5 className="subheader-italic">Transfer Table:</h5>
        <ul className="text">
          <li><span className="label">On Hand:</span> Total instances of a product within a department at a given time.  All eligible products can be displayed at once, or the report can be filtered based on the product’s consigned/owned status.</li>
          <li><span className="label">Used in Last Yr:</span> Total number of products “Used” within a rolling 365 days.</li>
          <li><span className="label">May Expire Unused:</span> Prediction of total number of products over PAR which may go to waste based on the previous year’s usage (i.e., Qty – (Par or expected demand, whichever is larger)).</li>
          <li><span className="label">Days to Soonest Expiration:</span> This number shows how many days remain before the next unit of this product expires.</li>
          <li><span className="label">Value:</span> Value of all products that may expire within the selected expiration period.</li>
          <li><span className="label">Price:</span> Current price for a given product.</li>
        </ul>

        <h5 className="subheader-italic">Transfer Options Table:</h5>
        <ul className="text">
          <li>
            <span className="label">Products to Transfer:</span> Product instances that are eligible to transfer to other locations within your health system.
            <ul>
              <li>The product's lot and expiration information, along with the quantity of these products, are displayed.</li>
              <li>The instances are ranked based on which will expire soonest.</li>
            </ul>
          </li>

          <li>
            <span className="label">Places to Transfer:</span> Locations eligible to receive excess inventory within the same hospital system.
            <ul>
              <li>
                Receiving department’s needs are based on:
                <ul>
                  <li>Their current on-hand quantity</li>
                  <li>Their distance from PAR</li>
                  <li>Having no quantities on order</li>
                  <li>Transfer amount will not cause the receiving department to exceed current PAR levels</li>
                </ul>
              </li>
              <li>
                Department needs are ranked based on the following priority levels:
                <ol>
                  <li>Critical: PAR &gt; 0, QOH = 0, QOO = 0</li>
                  <li>High Priority: PAR &gt; 0, QOH &gt; 0, QOO = 0 and (QOH + QOO) &lt; (PAR - ((Threshold × PAR) / 100))</li>
                  <li>High Priority: PAR &gt; 0, QOH = 0, QOO &gt; 0 and (QOH + QOO) &lt; (PAR - ((Threshold × PAR) / 100))</li>
                  <li>High Priority: PAR &gt; 0, (QOH + QOO) &lt; PAR and (QOH + QOO) &lt; (PAR - ((Threshold × PAR) / 100))</li>
                  <li>Normal Reorder: PAR &gt; 0, (QOH + QOO) &lt; PAR and (QOH + QOO) ≥ (PAR - ((Threshold × PAR) / 100))</li>
                  <li>Fully Stocked: PAR &gt; 0, (QOH + QOO) ≥ PAR</li>
                </ol>
              </li>
            </ul>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default UsageTransferDescription;